/**
 * Year6LowContentRecovery — recovery + urgency surfaces for low-content events.
 *
 * Three exports, one shared "Remind students" modal:
 *   1. LowContentRecoveryCard    — yellow recovery driver (Teacher Control Panel)
 *   2. LowContentNudgeBanner     — soft tip card    (Ready Page)
 *   3. AlmostReadyBoost          — green "almost ready" boost (Control Panel + Parent View)
 *
 * UX rule (per spec):
 *   "Don't apologise → Guide + motivate + create momentum."
 *   Never say "not enough content"; always frame positively.
 */
import React, { useState } from 'react';
import { toast } from 'sonner';
import { Lightning, Lightbulb, WhatsappLogo, Copy, X, Sparkle, Plus } from '@phosphor-icons/react';

const REMIND_MESSAGE = (link) =>
  `Add your photos and messages for our Year 6 Memory Book \u{1F393}\nIt only takes 30 seconds: ${link}`;


// ─────────────────────────────────────────────────────────────────
// 1. RECOVERY CARD — Teacher Control Panel (top, above missions)
// ─────────────────────────────────────────────────────────────────
export function LowContentRecoveryCard({ slug }) {
  const [remindOpen, setRemindOpen] = useState(false);
  const studentLink = typeof window !== 'undefined' ? `${window.location.origin}/e/${slug}` : '';

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(studentLink);
      toast.success('Student upload link copied');
    } catch { toast.error('Could not copy'); }
  };

  return (
    <>
      <div
        className="rounded-2xl p-4 mb-4 border bg-yellow-500/10 border-yellow-500/25 animate-[y6pulse_3s_ease-in-out_infinite]"
        data-testid="year6-low-content-recovery"
      >
        <p className="text-yellow-300 font-black text-[14.5px] inline-flex items-center gap-1.5 mb-1">
          <Lightning size={14} weight="fill" /> Your Memory Book needs a few more moments
        </p>
        <p className="text-white/70 text-[12.5px] leading-relaxed mb-3">
          Add just a few more photos and messages to make it feel complete.
        </p>
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={copyLink}
            className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg text-xs font-black text-black bg-yellow-300 hover:bg-yellow-400 transition-colors"
            data-testid="year6-low-content-copy-link"
          >
            <Copy size={12} weight="bold" /> Copy link to students
          </button>
          <button
            onClick={() => setRemindOpen(true)}
            className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg text-xs font-bold border border-white/15 bg-white/[0.04] hover:bg-white/[0.08] text-white"
            data-testid="year6-low-content-remind-class"
          >
            <Sparkle size={12} weight="fill" /> Remind class
          </button>
        </div>
      </div>

      {/* Local keyframes for the very-subtle pulse (Tailwind config-free) */}
      <style>{`@keyframes y6pulse { 0%, 100% { box-shadow: 0 0 0 0 rgba(234,179,8,0.0); } 50% { box-shadow: 0 0 0 6px rgba(234,179,8,0.08); } }`}</style>

      {remindOpen && (
        <RemindStudentsModal
          link={studentLink}
          onClose={() => setRemindOpen(false)}
        />
      )}
    </>
  );
}


// ─────────────────────────────────────────────────────────────────
// 2. SOFT NUDGE BANNER — Ready Page (under QR card)
// ─────────────────────────────────────────────────────────────────
export function LowContentNudgeBanner({ slug }) {
  const studentLink = typeof window !== 'undefined' ? `${window.location.origin}/e/${slug}` : '';
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(studentLink);
      toast.success('Student upload link copied');
    } catch { toast.error('Could not copy'); }
  };

  return (
    <div
      className="rounded-2xl p-4 mb-4 border border-white/10 bg-white/[0.03]"
      data-testid="year6-low-content-nudge"
    >
      <p className="text-white text-[14px] font-black inline-flex items-center gap-1.5 mb-1">
        <Lightbulb size={14} weight="fill" className="text-amber-300" />
        Tip: Add a few more memories to make your book even better
      </p>
      <p className="text-white/55 text-[12.5px] leading-relaxed mb-3">
        Most schools aim for 15–30 photos and 10+ messages.
      </p>
      <button
        onClick={copyLink}
        className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-[12.5px] font-black text-black bg-white hover:bg-neutral-100"
        data-testid="year6-low-content-nudge-copy"
      >
        <Copy size={12} weight="bold" /> Copy student link
      </button>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────
// 3. ALMOST-READY BOOST — Control Panel + Parent View
// ─────────────────────────────────────────────────────────────────
export function AlmostReadyBoost({ slug, testIdSuffix = '' }) {
  const studentLink = typeof window !== 'undefined' ? `${window.location.origin}/e/${slug}` : '';
  const tid = testIdSuffix ? `year6-almost-ready-boost-${testIdSuffix}` : 'year6-almost-ready-boost';

  return (
    <div
      className="rounded-2xl p-4 mb-4 border bg-emerald-500/8 border-emerald-500/25"
      data-testid={tid}
    >
      <p className="text-emerald-300 font-black text-[14.5px] mb-1">
        🎉 This Memory Book is almost ready
      </p>
      <p className="text-white/70 text-[12.5px] leading-relaxed mb-3">
        Just a few more moments will make it complete.
      </p>
      <a
        href={studentLink}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg text-xs font-black text-white"
        style={{ background: 'linear-gradient(135deg, #10B981, #059669)' }}
        data-testid={`${tid}-cta`}
      >
        <Plus size={12} weight="bold" /> Add a few more memories
      </a>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────
// Shared internal — Remind students modal
// ─────────────────────────────────────────────────────────────────
function RemindStudentsModal({ link, onClose }) {
  const message = REMIND_MESSAGE(link);
  const waUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;

  const copyMessage = async () => {
    try {
      await navigator.clipboard.writeText(message);
      toast.success('Message copied — paste in your class chat');
    } catch { toast.error('Could not copy'); }
  };

  return (
    <div
      className="fixed inset-0 z-[140] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
      onClick={onClose}
      data-testid="year6-remind-modal"
    >
      <div
        className="bg-neutral-900 border border-white/10 rounded-2xl w-full max-w-md p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-3">
          <div>
            <p className="text-[10px] font-black uppercase tracking-[0.18em] text-amber-300 mb-1">
              Quick message to send
            </p>
            <h3 className="text-white text-lg font-black">Remind your class</h3>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="p-2 rounded-full hover:bg-white/8 text-white/55 hover:text-white"
            data-testid="year6-remind-modal-close"
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        <pre
          className="rounded-xl bg-white/[0.04] border border-white/10 p-3.5 text-[12.5px] text-white/85 leading-relaxed whitespace-pre-wrap font-mono mb-4"
          data-testid="year6-remind-modal-message"
        >{message}</pre>

        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={copyMessage}
            className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg text-xs font-black bg-white text-neutral-900 hover:bg-neutral-100"
            data-testid="year6-remind-modal-copy"
          >
            <Copy size={13} weight="bold" /> Copy
          </button>
          <a
            href={waUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg text-xs font-black text-white"
            style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)' }}
            data-testid="year6-remind-modal-whatsapp"
          >
            <WhatsappLogo size={13} weight="fill" /> WhatsApp
          </a>
        </div>
      </div>
    </div>
  );
}
